import { IpaType } from "../phonemes/ipa";
import { Change, Condition, isNot, isOr, Not, Or } from "./change";

/** apply 'change' to 'input' & return the possibly modified phonemes + whether it changed */
export function applyChange(
  phonemes: IpaType[], change: Change
): { output: IpaType[], changed: boolean } {
  const output: IpaType[] = [];
  const inLen = phonemes.length;
  let changed = false;
  for (let i = 0; i < inLen; i++) {
    if (ignore(phonemes[i]) || !hasContext(phonemes, i, change.pre, change.post)) {
      output.push(phonemes[i]);
    } else {
      if (hasPostMatch(phonemes, i, change.input)) {
        change.output.forEach(p => output.push(p));
        i += change.input.length - 1;
        changed = true;
      } else {
        output.push(phonemes[i]);
      }
    }
  }
  return { output, changed };
}

/** check if the 'pre' & 'post' conditions are met */
function hasContext(phonemes: IpaType[], i: number, pre?: Condition, post?: Condition): boolean {
  if (pre && !hasPreMatch(phonemes, i - 1, pre)) {
    return false;
  }
  if (post && !hasPostMatch(phonemes, i + 1, post)) {
    return false;
  }
  return true;
}

/** check if phonemes[i] matches a pattern going forward */
function hasPostMatch(phonemes: IpaType[], i: number, pattern: Condition): boolean {
  if (pattern === '#boundary') {
    return i === phonemes.length; // matches iff we're at the end of the word
  }
  for (let j = 0; j < pattern.length; j++, i++) {
    if (ignore(phonemes[i])) {
      i++;
    }
    if (!isMatch(phonemes[i], i === phonemes.length, pattern[j])) {
      return false;
    }
  }
  return true;
}

/** check if phonemes[i] matches a pattern going backwards */
function hasPreMatch(phonemes: IpaType[], i: number, pattern: Condition): boolean {
  if (pattern === '#boundary') {
    return i === -1; // matches iff we're at the start of the word
  }
  for (let j = pattern.length - 1; j >= 0; j--, i--) {
    if (ignore(phonemes[i])) {
      i--;
    }
    if (!isMatch(phonemes[i], i === -1, pattern[j])) {
      return false;
    }
  }
  return true;
}

function isMatch(phoneme: IpaType, atBoundary: boolean, piece: IpaType | Or | Not): boolean {
  if (isOr(piece)) {
    let found = false;
    for (const p of piece.or) {
      if (p === '#boundary') {
        if (atBoundary) {
          found = true;
          break;
        }
      } else if (isNot(p)) {
        if (isOr(p.not)) {
          if (!isMatch(phoneme, atBoundary, p.not)) {
            found = true;
            break;
          }
        } else if (phoneme !== p.not) {
          found = true;
          break;
        }
      } else if (phoneme === p) {
        found = true;
        break;
      }
    }
    if (!found) {
      return false;
    }
  } else if (isNot(piece)) {
    return !isMatch(phoneme, atBoundary, piece.not);
  } else if (phoneme !== piece) {
    return false;
  }
  return true;
}

/** whether character should be ignored when matching patterns, but otherwise preserved */
function ignore(c: string): boolean {
  return c === '·';
}
